// One question, as chips. Lifted from the Andon app's `Question`.
//
// Shared there between the live card and the raise's round 0, and kept as one
// component here for the same reason: the thread screen in the next step of
// the merge puts the same question on the alert once it exists, and a second
// implementation would be a second set of affordances to keep in step (the
// camera, the search box, dictation, "Not sure").
//
// WHAT THE LIFT CHANGED. The camera is the shop floor's own `CameraScanner`
// (same contract, one BarcodeDetector sheet for the whole app), dictation is
// the shop floor's `useDictation` in the app's language with the app's
// remembered consent, and the words on screen are TR keys. The ANSWER text is
// not translated: "Not sure" travels in English because the server records
// and resolves it by its text, and the chip label is the only thing drawn.

import { useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import { CameraScanner } from '@/components/pod/CameraScanner';
import { Icon } from '@/components/ui/Icon';
import { useDictation } from '@/hooks/useDictation';
import { useT } from '@/i18n/useT';
import { andonKeys } from '@/queries/andon';
import { useStore } from '@/store';
import { SearchAnswer } from './SearchAnswer';

/**
 * The answer sent when the operator does not know. ENGLISH, ALWAYS.
 *
 * The server records it as typed and the question set suppresses its own
 * chip when a ranked option already means the same thing — both by matching
 * these words. A translated answer would be a new, unrecognised answer.
 */
export const NOT_SURE = 'Not sure';

/**
 * One follow-up: the model's chips, then the ones the interface guarantees.
 *
 * "Don't know" is a real answer — an operator who does not know is worth
 * recording, and it clears the question so the next one can be asked. It
 * travels as `Not sure`, the words the server resolves. "Something else"
 * opens a field, because the one thing the model did not think of is often
 * the thing that stopped the line. And on the raise, "Skip" (frame v2.12):
 * the question is left unanswered and unrecorded, and the alert asks it
 * again later — see `settled` in `lib/askFlow`.
 */
export function Question({
  question,
  options,
  scan,
  searchIn,
  minutes,
  searchOptions,
  freeTextHint,
  pending,
  onAnswer,
  onSkip,
  style,
}: {
  question: string;
  options: string[];
  /** Offer the camera. Only the identity question does — see below. */
  scan?: boolean;
  /**
   * Where the search box gets its rows, or null for a chips-only question.
   *
   * `parts` arrive with the question — forty rows at most, and meaningless to
   * any other andon. `subjects` is the five-hundred-row list of MOs, pod types
   * and projects, fetched once and cached across alerts because it is the same
   * answer for everybody and shipping it per alert would be a quarter of a
   * megabyte each.
   */
  searchIn?: 'subjects' | 'parts' | 'defects' | 'workcentres' | 'equipment' | null;
  /**
   * The answer is a number of minutes, so the free-text box is a number pad.
   *
   * Only the stop estimate uses it. Three windows cover most of what an
   * operator can actually judge — under ten minutes, about half an hour, about
   * two hours — but the fourth answer has to be a real number, because
   * "quarter to, when the render sets" is a thing people know and none of the
   * chips can say.
   */
  minutes?: boolean;
  searchOptions?: Array<{ label: string; detail: string | null }>;
  freeTextHint?: string | undefined;
  pending: boolean;
  onAnswer: (choice: string) => void;
  /** Offer Skip. Only the raise does; a question on the live alert is answered or left. */
  onSkip?: () => void;
  style?: CSSProperties;
}) {
  const t = useT();
  const [typing, setTyping] = useState(false);
  const [searching, setSearching] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Only fetched once the operator opens the box, and cached for the session
  // afterwards: it is the same five hundred rows for everybody, so a second
  // alert on the same tablet pays nothing. The defect vocabulary likewise —
  // there is no point holding 297 rows on a tablet that will raise three
  // materials andons today and no quality ones.
  //
  // There is deliberately no equivalent for `workcentres` or `equipment`.
  // Those rows are RANKED per raise — the stations this pod came through, the
  // blocked-by chain, the machines that could be the one that stopped — so
  // they travel with the question, and a full catalogue here would quietly
  // replace a ranked answer with an alphabetical one. Equipment is the
  // sharper case: the register is 1,148 rows of which nine are plant, so
  // alphabetical over it puts a Milwaukee battery at the top of a tile called
  // Machine down.
  const defects = useQuery({
    queryKey: andonKeys.defects,
    queryFn: () => andonApi.defects(),
    enabled: searching && searchIn === 'defects',
    // An hour. A defect vocabulary changes when somebody sits down and decides
    // to change it, which is not something an operator is waiting on.
    staleTime: 3_600_000,
  });
  const subjects = useQuery({
    queryKey: andonKeys.subjects,
    queryFn: () => andonApi.subjects(),
    enabled: searching && searchIn === 'subjects',
    staleTime: 60_000,
  });
  const [draft, setDraft] = useState('');
  const language = useStore((s) => s.language);
  const consent = useStore((s) => s.speechConsent);
  const dictation = useDictation(language);

  const chip = (label: string, onClick: () => void, muted = false) => (
    <button
      key={label}
      type="button"
      className="press"
      disabled={pending}
      onClick={onClick}
      style={{
        minHeight: 40,
        padding: '0 14px',
        borderRadius: 11,
        background: muted ? 'transparent' : 'var(--surface2)',
        border: muted ? '1px solid var(--line)' : 'none',
        color: muted ? 'var(--sub)' : 'var(--ink)',
        font: '600 12px Manrope,sans-serif',
        opacity: pending ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );

  // The transcript is a draft: it lands in the field for correction, never
  // straight to the server. Same idiom as the quality alert form.
  const toggleDictation = () => {
    if (dictation.listening) {
      dictation.stop();
      const heard = dictation.transcript.trim();
      if (heard) setDraft(heard.slice(0, 200));
    } else {
      dictation.start();
    }
  };

  const send = () => {
    // 200 characters is what the server accepts for an answer; anything longer
    // belongs in the thread, where a responder can reply to it.
    const text = draft.trim().slice(0, 200);
    if (!text || pending) return;
    onAnswer(text);
  };

  const placeholder =
    searchIn === 'subjects'
      ? t.qPhSubjectsT
      : searchIn === 'defects'
        ? t.qPhDefectsT
        : searchIn === 'workcentres'
          ? t.qPhWorkcentresT
          : searchIn === 'equipment'
            ? t.qPhEquipmentT
            : t.qPhPartsT;

  return (
    <div style={style}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
        <Icon name="info" size={15} style={{ color: 'var(--accent)', marginTop: 2 }} />
        <span style={{ font: '700 12.5px/1.5 Manrope,sans-serif', color: 'var(--ink)' }}>{question}</span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((option) => chip(option, () => onAnswer(option)))}
        {/*
          Scanning is offered before typing, and only where it beats it. A pod
          label carries its MO as a barcode, so for "which pod is this for?" the
          camera is faster and more accurate than a gloved hand on a keyboard —
          which is the whole reason that question is worth asking at all.
        */}
        {scan && chip(t.scanPod, () => setScanning(true), true)}
        {searchIn && !searching && chip(t.qSearchT, () => setSearching(true), true)}
        {/*
          "Not sure" is suppressed where the question already carries "Don't
          know" as a real, ranked answer. Two chips meaning the same thing side
          by side is not a choice, it is a moment of hesitation on a stopped
          line — and the two record differently, so whichever they tap is a
          coin flip in the data.
        */}
        {!options.some((o) => /^(don'?t know|not sure)$/i.test(o)) && chip(t.qDontKnowT, () => onAnswer(NOT_SURE), true)}
        {!typing && chip(minutes ? t.qGiveTimeT : t.qSomethingElseT, () => setTyping(true), true)}
        {onSkip && chip(t.qSkipT, onSkip, true)}
      </div>

      {searching && searchIn && (
        <SearchAnswer
          rows={
            // `parts` and `workcentres` travel WITH the question, because they
            // are ranked for this raise and mean nothing to another one. The
            // other two are shared lists the browser caches.
            searchIn === 'parts' || searchIn === 'workcentres' || searchIn === 'equipment'
              ? (searchOptions ?? [])
              : searchIn === 'defects'
                ? (defects.data?.options ?? []).map((o) => ({
                    // Area-qualified, and it has to match what the chips say
                    // exactly — the server resolves the answer by its text.
                    label: o.area ? `${o.area} · ${o.label}` : o.label,
                    detail: o.seen > 0 ? `${o.seen}${t.qRaisedHereT}` : null,
                  }))
                : (subjects.data?.options ?? []).map((o) => ({ label: o.label, detail: o.detail }))
          }
          placeholder={placeholder}
          loading={subjects.isLoading || defects.isLoading}
          pending={pending}
          onPick={(label) => onAnswer(label)}
          onFreeText={(text) => onAnswer(text)}
          // The machine list is a ranked shortlist; the whole register answers
          // what the shortlist does not. See `machineSearch` on the server.
          remote={searchIn === 'equipment' ? (term) => andonApi.equipment(term).then((r) => r.options) : undefined}
        />
      )}

      <CameraScanner
        open={scanning}
        onClose={() => setScanning(false)}
        onCode={(code) => {
          setScanning(false);
          onAnswer(code);
        }}
        onTypeInstead={() => {
          setScanning(false);
          setTyping(true);
        }}
      />

      {typing && (
        <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') send();
            }}
            autoFocus
            maxLength={minutes ? 4 : 200}
            // A number pad, not a keyboard. `inputMode` rather than
            // `type="number"` deliberately: the spinner arrows are unusable
            // with gloves on, and a number input silently discards anything it
            // cannot parse — an operator typing "45m" would watch the field
            // empty itself with no explanation.
            {...(minutes ? { inputMode: 'numeric' as const, pattern: '[0-9]*' } : {})}
            placeholder={freeTextHint ?? t.qSayWhatItIsT}
            aria-label={question}
            style={{
              flex: 1,
              minWidth: 0,
              height: 44,
              padding: '0 13px',
              borderRadius: 12,
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              color: 'var(--ink)',
              font: '500 13px Manrope,sans-serif',
              outline: 'none',
            }}
          />
          {dictation.supported && consent && (
            <button
              type="button"
              className="press"
              aria-label={dictation.listening ? t.stopT : t.voiceNote}
              aria-pressed={dictation.listening}
              onClick={toggleDictation}
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                display: 'grid',
                placeItems: 'center',
                flex: 'none',
                background: dictation.listening ? 'var(--danger)' : 'var(--surface2)',
                color: dictation.listening ? '#fff' : 'var(--sub)',
                animation: dictation.listening ? 'ipPulse 1.2s infinite' : 'none',
              }}
            >
              <Icon name="mic" size={17} />
            </button>
          )}
          <button
            type="button"
            className="press"
            aria-label={t.qSendAnswerT}
            disabled={pending || !draft.trim()}
            onClick={send}
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              background: 'var(--accent)',
              color: 'var(--accentInk)',
              opacity: pending || !draft.trim() ? 0.45 : 1,
            }}
          >
            <Icon name="send" size={17} />
          </button>
        </div>
      )}

      {typing && dictation.error && (
        <p role="status" style={{ margin: '6px 2px 0', font: '500 11px Manrope,sans-serif', color: 'var(--danger)' }}>
          {dictation.error}
        </p>
      )}
    </div>
  );
}
